import React, { useState, useEffect, useMemo, memo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import {
  ETAPAS_DE_CUSTO, INSUMOS_SUGERIDOS, UNIDADES, ROTULO_DA_CATEGORIA,
} from '../../../constants';
import {
  linhasAoAbrir, novaLinha, totalDaLinha, somaDasLinhas, custosParaGravarDetalhados,
} from '../../../utils/calculations/operationalCosts';
import { listInsumos } from '../../../data/insumos';

const reais = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const chave = (nome) => (nome || '').trim().toLowerCase();

const campo = 'w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

// Uma grade só para cabeçalho e linhas: é o que mantém as colunas alinhadas.
const GRADE = 'sm:grid sm:grid-cols-[minmax(0,1fr)_4.5rem_5rem_7rem_6.5rem_3.5rem_2rem] sm:gap-2 sm:items-center';

/**
 * Os custos do serviço, item por item.
 *
 * Eram cinco caixas fixas (km, material, diária, genética, sêmen), e "Material:
 * R$ 420" não dizia ao cliente o que ele pagava nem à equipe, um mês depois, o
 * que tinha sido cobrado. Agora é um cartão por etapa, como a tabela das
 * doadoras na OPU: uma linha por insumo, com quantidade, unidade, preço e se
 * repassa.
 *
 * O NOME PUXA O RESTO. Escolher um item do catálogo traz unidade, categoria e
 * o preço, quando o catálogo tiver. Qualquer outro nome vira linha avulsa —
 * não fica engessado no que está cadastrado.
 *
 * PREÇO É OPCIONAL. Uma linha sem preço não soma nada, mas fica registrada (e,
 * se o insumo controla estoque, baixa o saldo): às vezes o que se quer é só
 * anotar o que foi gasto.
 *
 * A LINHA TEM ALTURA ÚNICA. A categoria e o estoque moravam numa segunda
 * linha embaixo do item, e só ali — o item ficava mais alto que os números ao
 * lado e a tabela inteira saía torta. Foram para a dica do campo.
 */
const OperationalCostsModal = memo(({
  showModal,
  service,
  precoPorKmPadrao,
  laboratoryId,
  onSave,
  onClose,
  loading,
}) => {
  const [linhas, setLinhas] = useState([]);
  const [catalogo, setCatalogo] = useState([]);

  useEffect(() => {
    if (service && showModal) setLinhas(linhasAoAbrir(service));
  }, [service, showModal]);

  useEffect(() => {
    if (!showModal || !laboratoryId) return;
    let cancelado = false;
    listInsumos(laboratoryId)
      .then(lista => { if (!cancelado) setCatalogo((lista || []).filter(i => i.ativo)); })
      // Sem catálogo a tela continua servindo: linha avulsa e sugestões.
      .catch(err => console.error('Erro ao carregar insumos:', err));
    return () => { cancelado = true; };
  }, [showModal, laboratoryId]);

  /*
   * DE ONDE VÊM AS SUGESTÕES. Com catálogo, só dele: o laboratório que
   * excluiu o que não usa não quer ver o item voltar aqui. A lista sugerida
   * só aparece enquanto o catálogo está vazio, para a tela não nascer muda.
   */
  const opcoes = useMemo(() => (catalogo.length > 0
    ? catalogo
    : INSUMOS_SUGERIDOS.map(s => ({ ...s, sugerido: true }))), [catalogo]);

  const porNome = useMemo(
    () => new Map(opcoes.map(i => [chave(i.nome), i])), [opcoes]);

  const porId = useMemo(() => new Map(catalogo.map(i => [i.id, i])), [catalogo]);

  // As etapas que o serviço contratou, mais "Geral" — e qualquer etapa que já
  // tenha linha, para um custo lançado antes de descontratar não sumir da tela.
  const etapas = useMemo(() => {
    const oferecidos = service?.services_offered || [];
    const comLinha = new Set(linhas.map(l => l.etapa));
    return ETAPAS_DE_CUSTO.filter(e =>
      !e.requires || oferecidos.includes(e.requires) || comLinha.has(e.id));
  }, [service, linhas]);

  if (!showModal) return null;

  const mudar = (id, campos) =>
    setLinhas(atual => atual.map(l => (l.id === id ? { ...l, ...campos } : l)));

  const mudarNome = (linha, descricao) => {
    const achado = porNome.get(chave(descricao));
    if (!achado) {
      // Saiu de um item do catálogo para um nome livre: o vínculo vai junto,
      // senão a linha baixaria o estoque de algo que ela não é mais.
      mudar(linha.id, { descricao, insumo_id: null, ...(linha.insumo_id ? { categoria: 'outros' } : {}) });
      return;
    }
    const semPreco = linha.valor_unitario === null || linha.valor_unitario === '' || linha.valor_unitario === undefined;
    const preco = achado.preco ?? (achado.unidade === 'km' ? (Number(precoPorKmPadrao) || null) : null);
    mudar(linha.id, {
      descricao,
      insumo_id: achado.sugerido ? null : achado.id,
      unidade: achado.unidade,
      categoria: achado.categoria,
      ...(semPreco && preco !== null ? { valor_unitario: preco } : {}),
    });
  };

  const adicionar = (etapa) => setLinhas(atual => [...atual, novaLinha(etapa)]);
  const remover = (id) => setLinhas(atual => atual.filter(l => l.id !== id));

  const { custo, repasse } = somaDasLinhas(linhas);

  return (
    <JanelaModal chave="custos-operacionais" titulo="Custos do serviço"
      subtitulo={`${service?.service_code ? `#${service.service_code} · ` : ''}${service?.client_name || ''}`}
      onFechar={onClose} largura="sm:max-w-4xl">
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 bg-slate-50">
        {/* Uma lista por etapa, com os insumos dela primeiro. */}
        {etapas.map(etapa => (
          <datalist key={etapa.id} id={`insumos-${etapa.id}`}>
            {[...opcoes.filter(i => i.etapa === etapa.id), ...opcoes.filter(i => i.etapa !== etapa.id)]
              .map(i => <option key={i.nome} value={i.nome} />)}
          </datalist>
        ))}

        <div className="space-y-4">
          {etapas.map(etapa => {
            const daEtapa = linhas.filter(l => (l.etapa || 'geral') === etapa.id);
            const subtotal = somaDasLinhas(daEtapa);
            return (
              <section key={etapa.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <header className="flex items-baseline justify-between px-4 py-2.5 border-b border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-900">{etapa.rotulo}</h4>
                  <span className="text-xs text-slate-500 tabular-nums">
                    {daEtapa.length === 0 ? 'sem itens' : `R$ ${reais(subtotal.custo)}`}
                  </span>
                </header>

                {daEtapa.length > 0 && (
                  <div className={`hidden ${GRADE} px-4 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400`}>
                    <span>Item</span><span className="text-right">Qtd</span><span>Unid.</span>
                    <span className="text-right">Valor unit.</span><span className="text-right">Total</span>
                    <span className="text-center">Repassa</span><span />
                  </div>
                )}

                <ul>
                  {daEtapa.map(linha => {
                    const doCatalogo = linha.insumo_id ? porId.get(linha.insumo_id) : null;
                    const total = totalDaLinha(linha);
                    const unidades = UNIDADES.includes(linha.unidade) ? UNIDADES : [linha.unidade, ...UNIDADES];
                    const dica = [
                      ROTULO_DA_CATEGORIA[linha.categoria] || 'Outros',
                      doCatalogo?.controla_estoque
                        && `estoque: ${Number(doCatalogo.saldo).toLocaleString('pt-BR')} ${doCatalogo.unidade}`,
                    ].filter(Boolean).join(' · ');
                    return (
                      <li key={linha.id} className={`${GRADE} px-4 py-2 border-t border-slate-100 first:border-t-0`}>
                        <input list={`insumos-${etapa.id}`} className={`${campo} mb-2 sm:mb-0`}
                          placeholder="Escolha ou digite o item" title={dica}
                          aria-label="Item" value={linha.descricao}
                          onChange={(e) => mudarNome(linha, e.target.value)} />

                        {/* No celular, os números ficam numa linha embaixo do
                            item; no computador, `contents` devolve cada um à
                            sua coluna da grade. */}
                        <div className="grid grid-cols-[4.5rem_5rem_1fr] gap-2 sm:contents">
                          <input type="number" min="0" step="any" inputMode="decimal" className={`${campo} text-right`}
                            aria-label="Quantidade" value={linha.quantidade ?? ''}
                            onChange={(e) => mudar(linha.id, { quantidade: e.target.value })} />
                          <select className={campo} aria-label="Unidade" value={linha.unidade}
                            onChange={(e) => mudar(linha.id, { unidade: e.target.value })}>
                            {unidades.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">R$</span>
                            <input type="number" min="0" step="0.01" inputMode="decimal" className={`${campo} pl-7 text-right`}
                              aria-label="Valor unitário" placeholder="—" value={linha.valor_unitario ?? ''}
                              onChange={(e) => mudar(linha.id, { valor_unitario: e.target.value === '' ? null : e.target.value })} />
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-2 sm:mt-0 sm:contents">
                          <span className={`text-sm text-right tabular-nums ${total > 0 ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
                            {total > 0 ? `R$ ${reais(total)}` : '—'}
                          </span>
                          <label className="flex items-center justify-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                            <input type="checkbox" className="h-4 w-4" checked={!!linha.repassa}
                              onChange={(e) => mudar(linha.id, { repassa: e.target.checked })} />
                            <span className="sm:hidden">Repassa</span>
                          </label>
                          <button type="button" onClick={() => remover(linha.id)} title="Tirar o item"
                            aria-label="Tirar o item"
                            className="justify-self-end p-1.5 text-slate-400 hover:text-red-600 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <button type="button" onClick={() => adicionar(etapa.id)}
                  className="w-full flex items-center gap-1.5 px-4 py-2 text-sm text-blue-600 hover:bg-slate-50 border-t border-slate-100">
                  <Plus size={14} /> Adicionar item
                </button>
              </section>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 mt-4">
          Escolha da lista para trazer unidade e preço, ou digite qualquer nome. Sem preço, o item fica anotado e não soma.
          O catálogo fica na aba Insumos.
        </p>
      </div>

      <div className="border-t border-slate-200 px-4 sm:px-6 py-3 bg-white">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex gap-6 text-xs text-slate-500 tabular-nums mr-auto">
            <span>Custo<strong className="block text-base text-slate-900">R$ {reais(custo)}</strong></span>
            <span>Repassa ao cliente<strong className="block text-base text-slate-900">R$ {reais(repasse)}</strong></span>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button onClick={onClose} disabled={loading}
              className="flex-1 sm:flex-none px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-50 text-sm">
              Cancelar
            </button>
            <button
              onClick={() => onSave(custosParaGravarDetalhados(linhas))}
              disabled={loading}
              className="flex-1 sm:flex-none bg-blue-600 text-white py-2 px-5 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
            >
              {loading ? 'Salvando...' : 'Salvar custos'}
            </button>
          </div>
        </div>
      </div>
    </JanelaModal>
  );
});

export default OperationalCostsModal;
